import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isBoundedEvaluationWarehouseUser } from '../auth/access';
import { useAuth } from '../contexts/AuthContext';
import { ensureUiScope } from '../services/api';
import { reportsApi } from '../services/reportsApi';
import {
  extractEvaluationScopeFromSafetyCaseScope,
  getStaticProductEvaluatorEvaluationScope,
  isProductEvaluatorEvaluationScopePath,
  mergeEvaluationScopeIntoSearchParams,
} from '../utils/productEvaluatorScope';

/**
 * EPIC-165: default product evaluators to their assigned evaluation camera/device
 * on Live View, Zones, Policies, and Incidents when URL scope params are absent.
 */
export default function ProductEvaluatorScopeBootstrap() {
  const { effectiveUser: user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const appliedRef = useRef<string>('');

  useEffect(() => {
    if (!isBoundedEvaluationWarehouseUser(user)) return;
    if (!isProductEvaluatorEvaluationScopePath(location.pathname)) return;

    const params = new URLSearchParams(location.search || '');
    if (params.get('camera_id')) return;

    const cacheKey = `${location.pathname}?${params.toString()}`;
    if (appliedRef.current === cacheKey) return;

    const staticScope = getStaticProductEvaluatorEvaluationScope();
    const mergedStatic = mergeEvaluationScopeIntoSearchParams(user, params, staticScope);
    if (mergedStatic) {
      appliedRef.current = cacheKey;
      navigate(
        { pathname: location.pathname, search: mergedStatic.toString() },
        { replace: true },
      );
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const scope = ensureUiScope();
        const siteId = String(scope.siteId || params.get('site_id') || '').trim();
        if (!siteId) return;
        const res = await reportsApi.listSafetyCases({ limit: 25 });
        const items = Array.isArray(res?.items) ? res.items : [];
        const first = items.find((item) => item && typeof item === 'object') as
          | { scope?: Record<string, unknown> }
          | undefined;
        const evaluationScope = extractEvaluationScopeFromSafetyCaseScope(first?.scope);
        const merged = mergeEvaluationScopeIntoSearchParams(user, params, evaluationScope);
        if (cancelled || !merged) return;
        appliedRef.current = cacheKey;
        navigate(
          { pathname: location.pathname, search: merged.toString() },
          { replace: true },
        );
      } catch {
        // Best-effort; pages still load without camera scope.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, location.pathname, location.search, navigate]);

  return null;
}
